import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, Loader2, RefreshCcw, User, Users, Plus, X, Upload, UserPlus } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import {
    fetchCustomerList,
    currencyCodeToSymbolMap,
    fetchStoresWhereStaffAssigned,
    client,
    fetchGroups,
    fetchCustomerListExportCSV
} from '../apiHelper';
import { DataTable2 } from '../components/ui/DataTable2'; // ← NEW IMPORT
import { useNavigate } from 'react-router-dom';
import Input from '../components/ui/Input';
import TextArea from '../components/ui/TextArea';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Dropdown from '../components/ui/Dropdown';
import Button from '../components/ui/Button'; // Needed for potential future use
import TrialModal from '../components/ui/TrialModal';

const formatCurrency = (amount, currency) => {
    const symbol = currencyCodeToSymbolMap[currency] || currency;
    return `${symbol}${Number(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const PAGE_SIZE = 20;

const CustomerList = ({ onBack }) => {
    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsHasMore, setGroupsHasMore] = useState(true);
    const [groupsPage, setGroupsPage] = useState(0);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('list');
    const navigate = useNavigate();
    const [exporting, setExporting] = useState(false);

    // Modal state
    const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const [hideZeroBalance, setHideZeroBalance] = useState(true);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    useEffect(() => {
        const lcUser = localStorage.getItem('user');
        if (lcUser) setUser(JSON.parse(lcUser));
        else setError('User not found. Please log in.');
    }, []);

    const fetchCustomers = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const data = await fetchCustomerList(user);
            // console.log(data)
            setCustomers(data);
        } catch {
            showToast('Failed to load customers.', 'error');
        } finally {
            setLoading(false);
        }
    }, [user, showToast]);

    const handleExportCSV = async () => {
        if (!user) return;
        setExporting(true);
        try {
            const rawData = await fetchCustomerListExportCSV(user);

            // Create a blob from the raw CSV data
            const blob = new Blob([rawData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            // Create a temporary link to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `customer_list_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast('CSV Exported successfully', 'success');
        } catch (err) {
            console.error(err);
            showToast('Failed to export CSV.', 'error');
        } finally {
            setExporting(false);
        }
    };

    const handleExportPDF = () => {
        showToast('PDF Export coming soon!', 'info');
    };

    const fetchCustomerGroups = useCallback(
        async (reset = false) => {
            if (!user || groupsLoading) return;
            const offset = reset ? 0 : groupsPage * PAGE_SIZE;
            setGroupsLoading(true);
            try {
                const data = await fetchGroups({ offset, limit: PAGE_SIZE });
                if (reset) {
                    setGroups(data);
                    setGroupsPage(1);
                } else {
                    setGroups((prev) => [...prev, ...data]);
                    setGroupsPage((p) => p + 1);
                }
                setGroupsHasMore(data.length === PAGE_SIZE);
            } catch (err) {
                console.error(err);
                showToast('Failed to load groups.', 'error');
                setGroupsHasMore(false);
            } finally {
                setGroupsLoading(false);
            }
        },
        [user, groupsPage, groupsLoading, showToast]
    );

    const refreshGroups = () => {
        setGroups([]);
        setGroupsPage(0);
        setGroupsHasMore(true);
        fetchCustomerGroups(true);
    };

    const loadMoreGroups = () => {
        if (groupsHasMore && !groupsLoading) fetchCustomerGroups();
    };

    useEffect(() => {
        if (activeTab === 'list' && user) fetchCustomers();
        if (activeTab === 'groups' && user && groupsPage === 0) fetchCustomerGroups(true);
    }, [activeTab, user, groupsPage, fetchCustomers, fetchCustomerGroups]);

    useEffect(() => {
        if (isAddGroupModalOpen && user && stores.length === 0) {
            const load = async () => {
                setStoresLoading(true);
                try {
                    const data = await fetchStoresWhereStaffAssigned(user);
                    setStores(data.map((s) => ({ value: s.store_id, label: s.identifier })));
                } catch {
                    showToast('Failed to load stores.', 'error');
                } finally {
                    setStoresLoading(false);
                }
            };
            load();
        }
    }, [isAddGroupModalOpen, user, stores.length, showToast]);

    const handleAttachLineOfCredit = useCallback((row) => {
        showToast(`Attaching line of credit for: ${row.name || ''}`, 'info');
        navigate('/sales/customers/line-of-credit', {
            state: { customerId: row.users_id, customerName: row.name?.trim(), customerEmail: row.email },
        });
    }, [navigate, showToast]);

    const handleViewCustomerReport = useCallback((row) => {
        showToast(`Viewing report for: ${row.name || ''}`, 'info');
        navigate('/reports/customer-report', {
            state: { customerId: row.users_id, customerName: row.name?.trim(), customerEmail: row.email },
        });
    }, [navigate, showToast]);

    const handleRowAction = useCallback(
        (key, row) => {
            if (key === 'attach_line_of_credit') handleAttachLineOfCredit(row);
            else if (key === 'view_customer_report') handleViewCustomerReport(row);
            else if (key === 'export') showToast(`Exporting ${row.name}`, 'info');
        },
        [handleAttachLineOfCredit, handleViewCustomerReport, showToast]
    );

    const handleGroupAction = useCallback((action, group) => {
        if (action === 'upload_members') {
            navigate('/import-data', {
                state: {
                    target: 'customer-group-members',
                    groupId: group.mbrgrp_id,
                    groupName: group.name,
                },
            });
        } else if (action === 'assign_member') {
            showToast(`Assign member to "${group.name}" – coming soon!`, 'info');
        }
    }, [navigate, showToast]);

    // Customer List Columns
    const customerColumns = useMemo(
        () => [
            { key: 'customer_id', label: 'Customer ID' },
            { key: 'name', label: 'Customer Name' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone', render: (v) => v || '-' },
            {
                key: 'credit_limit',
                label: 'Credit Limit',
                render: (_, row) => {
                    const cl = row.creditlines?.[0];
                    return formatCurrency(cl?.credit_limit || 0, cl?.currency || 'NGN');
                },
            },
            {
                key: 'receivable_balance',
                label: 'Receivable Balance',
                render: (v, row) => formatCurrency(v || 0, row.creditlines?.[0]?.currency || 'NGN'),
            },
            {
                key: 'last_invoice_amount',
                label: 'Last Invoice',
                render: (v, row) => formatCurrency(v || 0, row.creditlines?.[0]?.currency || 'NGN'),
            },
            {
                key: 'last_payment_amount',
                label: 'Last Payment',
                render: (v, row) => formatCurrency(v || 0, row.creditlines?.[0]?.currency || 'NGN'),
            },
        ],
        []
    );

    // Customer Groups Columns
    const groupColumns = useMemo(
        () => [
            { key: 'mbrgrp_id', label: 'Group ID' },
            { key: 'name', label: 'Group Name' },
            {
                key: 'description',
                label: 'Description',
                render: (value) => value || '—',
            },
            {
                key: 'stores',
                label: 'Branches',
                render: (stores = []) =>
                    stores.length > 0 ? `${stores.length} branch${stores.length > 1 ? 'es' : ''}` : 'None',
            },
            {
                key: 'members',
                label: 'Customer Count',
                render: (members) => (members?.length || 0).toLocaleString(),
            },
        ],
        []
    );

    // Row actions for customers
    const customerRowActions = useMemo(
        () => [
            { label: 'View Report', key: 'view_customer_report' },
            { label: 'Export Row', key: 'export' },
            { label: 'Manage Line of Credit', key: 'attach_line_of_credit' },
        ],
        []
    );

    const displayedCustomers = useMemo(() => {
        if (!hideZeroBalance) return customers;

        return customers.filter((customer) => {
            const balance = parseFloat(customer.receivable_balance || '0');
            return balance !== 0 && !isNaN(balance);
        });
    }, [customers, hideZeroBalance]);

    const handleAddGroup = async () => {
        const errors = {};
        if (!groupName.trim()) errors.groupName = 'Group name is required';
        if (!selectedStore) errors.store = 'Please select a branch';
        setFormErrors(errors);
        if (Object.keys(errors).length) return;

        setSaving(true);
        try {
            const payload = {
                groupName: groupName.trim(),
                groupDescription: groupDescription.trim() || null,
                store_id: selectedStore,
                owner_id: user.users_id,
            };
            const response = await client.postData('/users/create-group', payload);
            const newGroup = {
                ...response,
                member_count: 0,
                created_at: new Date().toISOString(),
                stores: stores.find((s) => s.value === selectedStore)
                    ? [{ store_id: selectedStore, identifier: stores.find((s) => s.value === selectedStore).label }]
                    : [],
            };
            setGroups((prev) => [newGroup, ...prev]);
            showToast('Group created successfully!', 'success');
            closeAddGroupModal();
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to create group', 'error');
        } finally {
            setSaving(false);
        }
    };

    const openAddGroupModal = () => setIsAddGroupModalOpen(true);
    const closeAddGroupModal = () => {
        setIsAddGroupModalOpen(false);
        setGroupName('');
        setGroupDescription('');
        setSelectedStore('');
        setFormErrors({});
    };

    const handleImportClick = (target) => {
        navigate('/import-data', { state: { target } });
    };

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600">
                <p className="text-lg mb-4">{error}</p>
                {onBack && (
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        <ChevronLeft size={20} /> Go Back
                    </button>
                )}
            </div>
        );
    }

    const getTabClasses = (tab) =>
        `px-4 py-2 text-sm font-bold uppercase transition-colors ${activeTab === tab
            ? 'text-purple-700 border-b-2 border-purple-600'
            : 'text-gray-500 hover:text-gray-700'
        }`;

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <div className="mx-auto max-w-auto bg-white shadow-md rounded-lg overflow-hidden">
                <div className="pt-4 px-0 pb-0 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="text-gray-600 hover:text-purple-600 flex items-center gap-1 font-semibold"
                            >
                                <ChevronLeft size={20} /> Back to Dashboard
                            </button>
                        )}
                        <div className="flex-grow" />
                    </div>
                    <div className="flex gap-8">
                        <button onClick={() => setActiveTab('list')} className={getTabClasses('list')}>
                            Customer List
                        </button>
                        <button onClick={() => setActiveTab('groups')} className={getTabClasses('groups')}>
                            Customer Groups
                        </button>
                    </div>
                </div>

                <div className="min-h-[500px]">
                    {activeTab === 'list' && (
                        <>
                            {/* Optional: Move header actions outside if you want them above the table */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                                <div className="flex-grow">
                                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                        <User size={20} className="text-purple-600" /> Customer Data
                                    </h2>
                                    <p className="text-sm text-gray-500">A list of all customers and their details.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={hideZeroBalance}
                                            onChange={(e) => setHideZeroBalance(e.target.checked)}
                                            className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">
                                            {/* Hide customers with 0 balance */}
                                        </span>
                                    </label>
                                    <button
                                        onClick={fetchCustomers}
                                        disabled={loading}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                    >
                                        {loading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCcw size={20} />}
                                        <span>Refresh</span>
                                    </button>
                                    <Dropdown
                                        trigger={
                                            <button className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors">
                                                <Plus size={20} /> <span>New Customer</span>
                                            </button>
                                        }
                                        align="right"
                                    >
                                        <div className="py-1">
                                            <button
                                                type="button"
                                                onClick={() => navigate('/sales/customers/new')}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                                            >
                                                <UserPlus size={16} className="text-gray-600" />
                                                New Customer
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleImportClick('customers')}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 border-t border-gray-200"
                                            >
                                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5.5 5.5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                Import Customers
                                            </button>
                                        </div>
                                    </Dropdown>

                                    <button
                                        onClick={handleExportCSV}
                                        disabled={exporting || loading}
                                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                                    >
                                        {exporting ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} className="rotate-180" />}
                                        <span>CSV</span>
                                    </button>

                                    <button
                                        onClick={handleExportPDF}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 16l-4-4h3V4h2v8h3l-4 4zm9-8v10c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h4v2H5v10h14V8h-4V6h4c1.1 0 2 .9 2 2z" />
                                        </svg>
                                        <span>PDF</span>
                                    </button>

                                </div>
                            </div>

                            <DataTable2
                                columns={customerColumns}
                                // data={customers}
                                data={displayedCustomers}
                                loading={loading}
                                enableSearch={true}
                                enablePagination={true}
                                rowsPerPage={8}  // ← Better default
                                rowActions={customerRowActions}
                                onAction={handleRowAction}
                                emptyMessage={
                                    displayedCustomers.length === 0 && customers.length > 0 && hideZeroBalance
                                        ? "All customers have 0 receivable balance (hidden by filter)"
                                        : "No customers found."
                                }
                            // actionsHeader is now moved above for better layout
                            />
                        </>
                    )}

                    {activeTab === 'groups' && (
                        <>
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                                <div className="flex-grow">
                                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                        <Users size={20} className="text-purple-600" /> Manage Customer Groups
                                    </h2>
                                    <p className="text-sm text-gray-500">View and organize customer segments.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={refreshGroups}
                                        disabled={groupsLoading}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                    >
                                        {groupsLoading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCcw size={20} />}
                                        <span>Refresh</span>
                                    </button>
                                    <Dropdown
                                        trigger={
                                            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                                                <Plus size={20} /> <span>Add Group</span>
                                            </button>
                                        }
                                        align="right"
                                    >
                                        <div className="py-1">
                                            <button
                                                onClick={() => {
                                                    openAddGroupModal();
                                                    document.body.click();
                                                }}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                                            >
                                                <Users size={16} className="text-purple-600" />
                                                Add New Group
                                            </button>
                                            <button
                                                onClick={() => {
                                                    handleImportClick('customer-groups');
                                                    document.body.click();
                                                }}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 border-t border-gray-200"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5.5 5.5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                Import Groups
                                            </button>
                                        </div>
                                    </Dropdown>
                                </div>
                            </div>

                            <DataTable2
                                columns={groupColumns}
                                data={groups}
                                loading={groupsLoading && groups.length === 0}
                                enableSearch={true}
                                enablePagination={false}
                                emptyMessage="No customer groups found."
                                rowActions={[
                                    {
                                        label: 'Upload Members',
                                        key: 'upload_members',
                                        icon: <Upload size={16} />,
                                    },
                                    {
                                        label: 'Assign Member',
                                        key: 'assign_member',
                                        icon: <UserPlus size={16} />,
                                    },
                                ]}
                                onAction={handleGroupAction}
                            />

                            {groupsHasMore && (
                                <div className="mt-8 text-center pb-6">
                                    <button
                                        onClick={loadMoreGroups}
                                        disabled={groupsLoading}
                                        className="text-sm font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50"
                                    >
                                        {groupsLoading ? 'Loading more...' : 'Load More Groups'}
                                    </button>
                                </div>
                            )}
                            {!groupsHasMore && groups.length > 0 && (
                                <p className="mt-6 text-center text-sm text-gray-500 pb-6">
                                    End of list • {groups.length} group{groups.length !== 1 ? 's' : ''}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Add Group Modal - unchanged */}
            {isAddGroupModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-xl font-semibold">Add New Customer Group</h3>
                            <button onClick={closeAddGroupModal} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <Input
                                label="Group Name"
                                placeholder="e.g. VIP Customers"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                required
                                error={formErrors.groupName}
                            />
                            <TextArea
                                label="Description (optional)"
                                placeholder="Brief description..."
                                rows={4}
                                value={groupDescription}
                                onChange={(e) => setGroupDescription(e.target.value)}
                            />
                            <SearchableSelectBox
                                name="store"
                                label="Store / Branch"
                                placeholder="Search stores..."
                                value={selectedStore}
                                onChange={(e) => setSelectedStore(e.target.value)}
                                options={stores}
                                loading={storesLoading}
                                required
                                error={formErrors.store}
                            />
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t">
                            <button
                                onClick={closeAddGroupModal}
                                disabled={saving}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddGroup}
                                disabled={saving}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving && <Loader2 size={18} className="animate-spin" />}
                                {saving ? 'Creating...' : 'Create Group'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default CustomerList;